import { Link, useLocation } from 'react-router-dom'
import { clearSessionAndRedirectToLogin } from '../../config/api'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  LayoutDashboard,
  Calendar,
  Coffee,
  Users,
  Briefcase,
  BarChart2,
  Settings,
  LogOut,
  ChevronsUpDown,
  PanelLeft,
  ChevronRight,
  ExternalLink,
  Search,
  type LucideIcon,
} from 'lucide-react'

interface DashboardLayoutProps {
  children: ReactNode
}

interface DashboardUser {
  name?: string
  email?: string
}

interface NavLink {
  label: string
  path: string
  icon: LucideIcon
  children?: { label: string; path: string }[]
}

interface NavGroup {
  label: string
  items: NavLink[]
}

const navGroups: NavGroup[] = [
  {
    label: 'Übersicht',
    items: [{ icon: LayoutDashboard, label: 'Übersicht', path: '/dashboard' }],
  },
  {
    label: 'Inhalte',
    items: [
      { icon: Calendar, label: 'Events', path: '/dashboard/events' },
      { icon: Coffee, label: 'Brunch', path: '/dashboard/brunches' },
    ],
  },
  {
    label: 'Kommunikation',
    items: [{ icon: Briefcase, label: 'Bewerbung', path: '/dashboard/bewerbung' }],
  },
  {
    label: 'Statistik',
    items: [
      {
        icon: BarChart2,
        label: 'Statistiken',
        path: '/dashboard/statistik',
        children: [
          { label: 'Übersicht', path: '/dashboard/statistik' },
          { label: 'Events & Partys', path: '/dashboard/statistik/partys' },
          { label: 'Brunch', path: '/dashboard/statistik/brunch' },
        ],
      },
    ],
  },
  {
    label: 'System',
    items: [
      { icon: Users, label: 'Benutzer', path: '/dashboard/users' },
      { icon: Settings, label: 'Einstellungen', path: '/dashboard/settings' },
    ],
  },
]

const crumbLabels: Record<string, string> = {
  '/dashboard': 'Übersicht',
  '/dashboard/events': 'Events',
  '/dashboard/brunches': 'Brunch',
  '/dashboard/bewerbung': 'Bewerbung',
  '/dashboard/statistik': 'Statistiken',
  '/dashboard/statistik/partys': 'Events & Partys',
  '/dashboard/statistik/brunch': 'Brunch',
  '/dashboard/users': 'Benutzer',
  '/dashboard/settings': 'Einstellungen',
}

function normalizePath(pathname: string) {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1)
  return pathname
}

function isPathActive(path: string, pathname: string) {
  const current = normalizePath(pathname)
  if (path === '/dashboard') return current === '/dashboard'
  if (path === '/dashboard/statistik') return current === '/dashboard/statistik'
  return current === path
}

function isGroupActive(item: NavLink, pathname: string) {
  const current = normalizePath(pathname)
  if (item.children?.length) {
    return current === item.path || current.startsWith(`${item.path}/`)
  }
  return isPathActive(item.path, pathname)
}

function initials(user: DashboardUser | null) {
  const source = user?.name?.trim() || user?.email?.trim() || 'A'
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  return source.slice(0, 2).toUpperCase()
}

interface SearchEntry {
  label: string
  path: string
  group: string
}

const searchEntries: SearchEntry[] = navGroups.flatMap((group) =>
  group.items.flatMap((item) =>
    item.children?.length
      ? item.children.map((child) => ({ label: child.label, path: child.path, group: item.label }))
      : [{ label: item.label, path: item.path, group: group.label }],
  ),
)

function SearchCommand({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    setQuery('')
    const frame = requestAnimationFrame(() => inputRef.current?.focus())
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  const normalized = query.trim().toLowerCase()
  const results = searchEntries.filter((entry) =>
    `${entry.label} ${entry.group}`.toLowerCase().includes(normalized),
  )

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-zinc-950/40 p-4 pt-[12vh]">
      <button type="button" aria-label="Suche schließen" className="absolute inset-0" onClick={onClose} />
      <div className="relative w-full max-w-lg overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-lg">
        <div className="flex items-center gap-2 border-b border-zinc-200 px-3">
          <Search className="size-4 shrink-0 text-zinc-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Seite suchen…"
            className="h-11 w-full bg-transparent text-sm text-zinc-950 outline-none placeholder:text-zinc-400"
          />
          <kbd className="hidden rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500 sm:inline">
            ESC
          </kbd>
        </div>
        <ul className="max-h-80 overflow-y-auto p-1">
          {results.length === 0 ? (
            <li className="px-3 py-8 text-center text-sm text-zinc-500">Keine Seite gefunden.</li>
          ) : (
            results.map((entry) => (
              <li key={entry.path}>
                <Link
                  to={entry.path}
                  onClick={onClose}
                  className="flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm hover:bg-zinc-100"
                >
                  <span className="font-medium text-zinc-950">{entry.label}</span>
                  <span className="truncate text-xs text-zinc-500">{entry.group}</span>
                </Link>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  )
}

function breadcrumbs(pathname: string) {
  const current = normalizePath(pathname)
  const label = crumbLabels[current] ?? 'Dashboard'
  if (current.startsWith('/dashboard/statistik/') && crumbLabels[current]) {
    return [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Statistiken', path: '/dashboard/statistik' },
      { label, path: current },
    ]
  }
  if (current === '/dashboard') {
    return [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Übersicht', path: '/dashboard' },
    ]
  }
  return [
    { label: 'Dashboard', path: '/dashboard' },
    { label, path: current },
  ]
}

function SidebarNav({
  collapsed,
  pathname,
  onNavigate,
}: {
  collapsed: boolean
  pathname: string
  onNavigate?: () => void
}) {
  const statsActive = normalizePath(pathname).startsWith('/dashboard/statistik')
  const [statsOpen, setStatsOpen] = useState(statsActive)

  useEffect(() => {
    if (statsActive) setStatsOpen(true)
  }, [statsActive])

  return (
    <nav className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-2 py-2">
      {navGroups.map((group) => (
        <div key={group.label} className="flex flex-col gap-1">
          <p
            className={`px-2 text-[11px] font-medium uppercase tracking-wide text-zinc-500 ${
              collapsed ? 'sr-only' : ''
            }`}
          >
            {group.label}
          </p>
          {group.items.map((item) => {
            const Icon = item.icon
            const active = isGroupActive(item, pathname)

            if (item.children?.length) {
              if (collapsed) {
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={item.label}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    className={`flex h-8 items-center justify-center rounded-md transition-colors ${
                      active
                        ? 'bg-white/10 text-white'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-100'
                    }`}
                  >
                    <Icon className="size-4" />
                  </Link>
                )
              }

              return (
                <div key={item.path}>
                  <button
                    type="button"
                    onClick={() => setStatsOpen((open) => !open)}
                    aria-expanded={statsOpen}
                    className={`flex h-8 w-full items-center gap-2 rounded-md px-2 text-sm font-medium transition-colors ${
                      active
                        ? 'bg-white/10 text-white'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-100'
                    }`}
                  >
                    <Icon className="size-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                    <ChevronRight
                      className={`ml-auto size-4 shrink-0 text-zinc-500 transition-transform ${
                        statsOpen ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                  {statsOpen && (
                    <div className="mt-1 ml-4 flex flex-col gap-1 border-l border-white/10 pl-2">
                      {item.children.map((child) => {
                        const childActive = isPathActive(child.path, pathname)
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={onNavigate}
                            aria-current={childActive ? 'page' : undefined}
                            className={`flex h-8 items-center rounded-md px-2 text-sm transition-colors ${
                              childActive
                                ? 'bg-white/10 font-medium text-white'
                                : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-100'
                            }`}
                          >
                            <span className="truncate">{child.label}</span>
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                title={collapsed ? item.label : undefined}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                className={`flex h-8 items-center gap-2 rounded-md text-sm font-medium transition-colors ${
                  collapsed ? 'justify-center px-0' : 'px-2'
                } ${
                  active
                    ? 'bg-white/10 text-white'
                    : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-100'
                }`}
              >
                <Icon className="size-4 shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            )
          })}
        </div>
      ))}
    </nav>
  )
}

function UserMenu({
  user,
  collapsed,
  onLogout,
  onNavigate,
}: {
  user: DashboardUser | null
  collapsed: boolean
  onLogout: () => void
  onNavigate?: () => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const name = user?.name || 'Admin'
  const email = user?.email || ''

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative border-t border-white/10 p-2">
      {open && (
        <div
          className={`absolute z-50 rounded-lg border border-white/10 bg-zinc-900 p-1 shadow-lg ${
            collapsed ? 'bottom-0 left-full ml-2 w-56' : 'right-2 bottom-full left-2 mb-2'
          }`}
        >
          <div className="px-2 py-1.5">
            <p className="truncate text-sm font-medium text-white">{name}</p>
            {email && <p className="truncate text-xs text-zinc-400">{email}</p>}
          </div>
          <div className="my-1 h-px bg-white/10" />
          <Link
            to="/"
            onClick={() => {
              setOpen(false)
              onNavigate?.()
            }}
            className="flex h-8 items-center gap-2 rounded-md px-2 text-sm text-zinc-200 hover:bg-white/5"
          >
            <ExternalLink className="size-4" />
            Zur Website
          </Link>
          <Link
            to="/dashboard/settings"
            onClick={() => {
              setOpen(false)
              onNavigate?.()
            }}
            className="flex h-8 items-center gap-2 rounded-md px-2 text-sm text-zinc-200 hover:bg-white/5"
          >
            <Settings className="size-4" />
            Einstellungen
          </Link>
          <div className="my-1 h-px bg-white/10" />
          <button
            type="button"
            onClick={onLogout}
            className="flex h-8 w-full items-center gap-2 rounded-md px-2 text-sm text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="size-4" />
            Abmelden
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={collapsed ? name : undefined}
        className={`flex w-full items-center gap-2 rounded-md p-1.5 text-left hover:bg-white/5 ${
          collapsed ? 'justify-center' : ''
        }`}
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-800 text-xs font-semibold text-white">
          {initials(user)}
        </span>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-white">{name}</span>
              <span className="block truncate text-xs text-zinc-500">{email || 'Angemeldet'}</span>
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-zinc-500" />
          </>
        )}
      </button>
    </div>
  )
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const location = useLocation()
  const [currentUser, setCurrentUser] = useState<DashboardUser | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const closeSearch = useCallback(() => setSearchOpen(false), [])

  useEffect(() => {
    const stored = localStorage.getItem('dashboard_sidebar_collapsed')
    if (stored === 'true') setCollapsed(true)
    const userStr = localStorage.getItem('dashboard_user')
    if (!userStr) return
    try {
      setCurrentUser(JSON.parse(userStr) as DashboardUser)
    } catch (error) {
      console.error('Error parsing user data:', error)
    }
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setSearchOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setSearchOpen(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      const next = !value
      localStorage.setItem('dashboard_sidebar_collapsed', String(next))
      return next
    })
  }

  const crumbs = breadcrumbs(location.pathname)

  const sidebar = (isCollapsed: boolean, onNavigate?: () => void) => (
    <div className="flex h-full min-h-0 flex-col">
      <div className={`flex h-14 shrink-0 items-center ${isCollapsed ? 'justify-center px-2' : 'px-3'}`}>
        <Link to="/dashboard" onClick={onNavigate} className="flex min-w-0 items-center gap-2">
          <span className={`flex items-center overflow-hidden ${isCollapsed ? 'size-8' : 'h-8'}`}>
            <img src="/Logo_HM.png" alt="Hückels May" className="h-8 w-auto max-w-none" />
          </span>
          {!isCollapsed && (
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-white">Hückels May</span>
              <span className="block truncate text-xs text-zinc-500">Dashboard</span>
            </span>
          )}
        </Link>
      </div>
      <SidebarNav collapsed={isCollapsed} pathname={location.pathname} onNavigate={onNavigate} />
      <UserMenu
        user={currentUser}
        collapsed={isCollapsed}
        onLogout={clearSessionAndRedirectToLogin}
        onNavigate={onNavigate}
      />
    </div>
  )

  return (
    <div className="dashboard-app flex h-svh w-full overflow-hidden bg-zinc-900 font-sans text-zinc-100 antialiased">
      <aside
        className={`hidden h-svh shrink-0 flex-col bg-zinc-900 transition-[width] duration-200 ease-linear md:flex ${
          collapsed ? 'w-[52px]' : 'w-64'
        }`}
      >
        {sidebar(collapsed)}
      </aside>

      {mobileOpen && (
        <button
          type="button"
          aria-label="Menü schließen"
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-zinc-900 shadow-xl transition-transform duration-200 md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebar(false, () => setMobileOpen(false))}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col md:py-2 md:pr-2">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white text-zinc-950 md:rounded-xl md:border md:border-zinc-200 md:shadow-sm">
          <header className="flex h-14 shrink-0 items-center gap-2 border-b border-zinc-200 px-3 sm:px-4">
            <button
              type="button"
              onClick={() => {
                if (window.matchMedia('(min-width: 768px)').matches) toggleCollapsed()
                else setMobileOpen(true)
              }}
              className="inline-flex size-8 items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"
              aria-label={collapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'}
            >
              <PanelLeft className="size-4" />
            </button>
            <div className="mx-1 hidden h-4 w-px bg-zinc-200 sm:block" />
            <nav aria-label="Brotkrumen" className="flex min-w-0 flex-1 items-center gap-1.5 text-sm">
              {crumbs.map((crumb, index) => {
                const last = index === crumbs.length - 1
                return (
                  <span key={`${crumb.path}-${crumb.label}`} className="flex min-w-0 items-center gap-1.5">
                    {index > 0 && <span className="text-zinc-300">/</span>}
                    {last ? (
                      <span className="truncate font-medium text-zinc-950">{crumb.label}</span>
                    ) : (
                      <Link to={crumb.path} className="truncate text-zinc-500 hover:text-zinc-800">
                        {crumb.label}
                      </Link>
                    )}
                  </span>
                )
              })}
            </nav>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="inline-flex h-8 items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 text-sm text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
            >
              <Search className="size-3.5" />
              <span className="hidden sm:inline">Suchen</span>
              <kbd className="hidden rounded border border-zinc-200 bg-white px-1 text-[10px] font-medium text-zinc-400 md:inline">
                ⌘K
              </kbd>
            </button>
          </header>
          <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-zinc-50/60 p-4 sm:p-6">
            {children}
          </main>
        </div>
      </div>
      <SearchCommand open={searchOpen} onClose={closeSearch} />
    </div>
  )
}
